import {Directive, ElementRef, EventEmitter, inject, NgZone, OnDestroy, OnInit, Output} from '@angular/core';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';

/**
 * Directive that detects when a scroll container is near the bottom
 * using the IntersectionObserver API. Emits an event when a sentinel element
 * becomes visible, indicating the user has scrolled near the end.
 *
 * Usage:
 * ```html
 * <div appInfiniteScroll (infiniteScroll)="onLoadMore()" class="scroll-container">
 *   <div *ngFor="let item of items">{{ item }}</div>
 *   <div appInfiniteScrollSentinel></div>
 * </div>
 * ```
 */
@Directive({
  selector: '[appInfiniteScroll]',
  standalone: true,
})
export class InfiniteScrollDirective implements OnInit, OnDestroy {
  @Output() infiniteScroll = new EventEmitter<InfiniteScrollDirective>();

  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private readonly ngZone = inject(NgZone);
  private intersectionObserver: IntersectionObserver | null = null;
  private readonly destroy$ = new Subject<void>();
  private observerSetup = false;
  /** True while a load is in-flight; prevents duplicate emissions. */
  private isLoading = false;

  ngOnInit() {
    this.ngZone.onStable.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (this.observerSetup) {
        // Re-observe the sentinel after each stable state
        // Doing so ensures that if the sentinel
        // is still visible after a new load of results (e.g. when zoomed out), the
        // IntersectionObserver fires again and triggers another fetch.
        // Only do so when a load is not already in-flight, so that a single
        // stable event emitted mid-fetch does not trigger a second fetch.
        if (!this.isLoading) {
          this.reobserveSentinel();
        }
      } else {
        this.setupIntersectionObserver();
        this.observerSetup = true;
      }
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();

    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
      this.intersectionObserver = null;
    }
  }

  reobserveSentinel() {
    if (!this.intersectionObserver) return;
    const sentinel = this.elementRef.nativeElement.querySelector('[appInfiniteScrollSentinel]');
    if (!sentinel) return;
    // Force the IntersectionObserver to re-evaluate
    // whether the sentinel is visible, triggering a fetch if it is still in view.
    this.intersectionObserver.unobserve(sentinel);
    this.intersectionObserver.observe(sentinel);
  }

  private setupIntersectionObserver() {
    const sentinel = this.elementRef.nativeElement.querySelector('[appInfiniteScrollSentinel]');

    if (!sentinel) {
      console.warn('InfiniteScrollDirective: No sentinel element found with appInfiniteScrollSentinel');
      return;
    }

    this.intersectionObserver = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !this.isLoading) {
            this.isLoading = true;
            this.ngZone.run(() => {
              this.infiniteScroll.emit(this);
            });
          }
        });
      },
      {
        root: null,
        rootMargin: '0px 0px 185px 0px', // trigger 1 minimal-height tile early
        threshold: 0.1,
      },
    );

    this.intersectionObserver.observe(sentinel);
  }

  /**
   * Called by the host after the triggered load has completed (or failed).
   * Resets the loading flag so that the next stable event will re-arm the observer.
   */
  markLoadComplete() {
    this.isLoading = false;
  }
}

@Directive({
  selector: '[appInfiniteScrollSentinel]',
  standalone: true,
})
export class InfiniteScrollSentinelDirective {}
